'use client';

import { clsx } from 'clsx';
import { AnimatePresence } from 'motion/react';
import * as m from 'motion/react-m';
import { FC, useState } from 'react';

import { LazyMotionWrapperFull } from '../LazyMotionWrapper/LazyMotionWrapperFull';
import { ScrollBox } from '../ScrollBox/ScrollBox';
import { Tab } from './Tabs.type';

type Props = {
  tabsId: string;
  tabs: Tab[];
  variant?: 'default' | 'vehicle-carousel';
  fullWidth?: boolean;
};

export const Tabs: FC<Props> = ({ tabsId, tabs, variant = 'default', fullWidth = false }) => {
  const [activeTab, setActiveTab] = useState<string>(tabs[0]?.id ?? '');
  const tab = tabs.find((item) => item.id === activeTab);

  if (tabs.length === 0) {
    return null;
  }

  return (
    <div
      className={clsx({
        'mb-6': variant === 'vehicle-carousel',
      })}
    >
      <div
        role="tablist"
        className={clsx('group/tab-list relative w-full border-b-2 border-bor-neu-bas', {
          'mb-6': variant === 'vehicle-carousel',
        })}
      >
        <LazyMotionWrapperFull>
          {fullWidth ? (
            <div className="flex justify-start">
              <TabList tabsId={tabsId} tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} fullWidth />
            </div>
          ) : (
            <ScrollBox
              className={clsx(
                'snap-x-mandatory -mb-0.5 flex items-end justify-start overflow-y-hidden pb-0.5',
                '[-ms-overflow-style:none] [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
                {
                  '-mx-5 px-5 sm:-mx-8 sm:px-8': variant === 'vehicle-carousel',
                  'mx-0 px-0': variant === 'default',
                },
              )}
            >
              <TabList tabsId={tabsId} tabs={tabs} activeTab={activeTab} setActiveTab={setActiveTab} />
            </ScrollBox>
          )}
        </LazyMotionWrapperFull>
        <div
          className={clsx(
            'pointer-events-none absolute top-0 h-full',
            {
              '-right-5 -left-5': variant === 'vehicle-carousel',
            },
            'before:absolute before:top-0 before:-left-0.5 before:block before:h-full before:w-9 before:opacity-0 before:transition-opacity before:duration-200 before:ease-in-out before:content-[""]',
            'before:bg-gradient-to-r before:from-white before:to-transparent',
            'after:absolute after:top-0 after:-right-0.5 after:block after:h-full after:w-9 after:opacity-0 after:transition-opacity after:duration-200 after:ease-in-out after:content-[""]',
            'after:bg-gradient-to-l after:from-white after:to-transparent',
            'sm:before:w-16 sm:after:w-16',
            'md:before:w-18 md:after:w-18',
            'group-has-[.overflow-right]/tab-list:after:opacity-100',
            'group-has-[.overflow-left]/tab-list:before:opacity-100',
          )}
        ></div>
      </div>
      {!!tab && !!tab.content && (
        <div role="tabpanel" id={`panel-${tab.id}`} aria-labelledby={`tab-${tab.id}`}>
          {tab.content}
        </div>
      )}
    </div>
  );
};

type TabListProps = {
  tabsId: string;
  tabs: Tab[];
  activeTab: string;
  setActiveTab: (tab: string) => void;
  fullWidth?: boolean;
};

const TabList: FC<TabListProps> = ({ tabsId, tabs, activeTab, setActiveTab, fullWidth = false }) => {
  return (
    <>
      {tabs.map((item) => {
        const isActive = item.id === activeTab;
        return (
          <button
            key={item.id}
            role="tab"
            className={clsx(
              'relative flex cursor-pointer snap-center appearance-none flex-col items-center justify-end border-none p-4 whitespace-nowrap',
              {
                'bg-bac-tie1 font-bold': isActive,
                'bg-transparent': !isActive,
                'flex-1': fullWidth,
              },
            )}
            id={`tab-${item.id}`}
            data-testid={`tab-${item.id}`}
            aria-controls={`panel-${item.id}`}
            aria-selected={isActive ? 'true' : 'false'}
            onClick={() => {
              setActiveTab(item.id);
              item.onClick?.();
            }}
          >
            {!!item.icon && <span className="relative block h-6 w-12">{item.icon}</span>}
            <span className="c-fs-body2 tabs-tab-label">{item.title}</span>
            <div className="absolute -bottom-0.5 left-0 h-0.5 w-full bg-bor-neu-bas"></div>
            <AnimatePresence>
              {isActive && (
                <m.div
                  layout
                  className="absolute -bottom-0.5 left-0 h-0.5 w-full bg-bor-pri-bas"
                  layoutId={`${tabsId}-underline`}
                  transition={{ duration: 0.3 }}
                />
              )}
            </AnimatePresence>
          </button>
        );
      })}
    </>
  );
};
